import { memo, useState, useCallback, useEffect } from 'react'
import { programsApi, programGenresApi, collectionsApi, seriesApi } from '../../lib/api.js'
import {
  BULK_SKIP_LABELS,
  type BulkProgramAction,
  type BulkProgramResult,
  type ProgramGenre,
  type Collection,
  type Series,
} from '../../types/domain.js'
import { ModalShell } from '../common/ModalShell.js'

/**
 * Actions groupées d'une sélection de programmes.
 *
 * Deux principes tiennent l'écran :
 *
 * - **Annoncer avant d'écrire.** Chaque action passe d'abord en `dryRun` : le
 *   serveur rend ce qu'il toucherait, et l'opérateur confirme sur des nombres
 *   réels plutôt que sur le nombre de lignes cochées. Poser un genre sur deux
 *   cents fiches ne se rattrape pas d'un Ctrl+Z.
 * - **Nommer ce qui est écarté.** Une fiche ignorée sort avec son motif ; sans
 *   ça, l'opérateur croit avoir traité toute sa sélection alors qu'une partie
 *   est restée en arrière.
 */

type Draft =
  | { kind: 'GENRE'; programGenreId: string; subGenre: string }
  | { kind: 'AIR_DATE'; date: string }
  | { kind: 'SERIES'; seriesId: string }
  | { kind: 'COLLECTION'; collectionId: string; mode: 'ADD' | 'REMOVE' }
  | { kind: 'ARCHIVE' }
  | { kind: 'RESTORE' }

const DRAFT_TITLES: Record<Draft['kind'], string> = {
  GENRE:      'Changer le genre',
  AIR_DATE:   'Date de première diffusion',
  SERIES:     'Rattacher à une série',
  COLLECTION: 'Collections',
  ARCHIVE:    'Archiver les programmes',
  RESTORE:    'Désarchiver les programmes',
}

function toAction(draft: Draft): BulkProgramAction | null {
  switch (draft.kind) {
    case 'GENRE':
      if (!draft.programGenreId) return null
      return {
        kind: 'UPDATE',
        // Le sous-genre n'accompagne le genre que s'il est saisi : un champ
        // laissé vide ne doit pas effacer les précisions déjà portées par les
        // fiches sélectionnées.
        changes: draft.subGenre.trim()
          ? { programGenreId: draft.programGenreId, subGenre: draft.subGenre.trim() }
          : { programGenreId: draft.programGenreId },
      }
    case 'AIR_DATE':
      return { kind: 'UPDATE', changes: { plannedFirstAirDate: draft.date || null } }
    case 'SERIES':
      return { kind: 'UPDATE', changes: { seriesId: draft.seriesId || null } }
    case 'COLLECTION':
      if (!draft.collectionId) return null
      return draft.mode === 'ADD'
        ? { kind: 'ADD_TO_COLLECTION', collectionId: draft.collectionId }
        : { kind: 'REMOVE_FROM_COLLECTION', collectionId: draft.collectionId }
    case 'ARCHIVE':
      return { kind: 'ARCHIVE' }
    case 'RESTORE':
      return { kind: 'RESTORE' }
  }
}

interface ProgramBulkActionsProps {
  channelId: string
  selectedIds: string[]
  /** Appelé après une écriture réussie : la liste appelante se recharge. */
  onDone: () => void
  canDelete: boolean
  /**
   * Propose le rattachement à une série.
   *
   * Réservé au catalogue : depuis la fiche d'une série, l'action déplacerait les
   * épisodes vers une *autre* série, geste qu'on ne vient pas chercher là et qui
   * viderait la fiche ouverte sous les yeux de l'opérateur.
   */
  allowSeriesChange?: boolean
  /**
   * La sélection contient au moins une fiche archivée : le bouton
   * « Désarchiver » n'apparaît qu'alors. L'offrir sur une sélection d'actifs
   * proposerait un geste sans effet.
   */
  selectionHasArchived?: boolean
}

export const ProgramBulkActions = memo(function ProgramBulkActions({
  channelId, selectedIds, onDone, canDelete, allowSeriesChange = false,
  selectionHasArchived = false,
}: ProgramBulkActionsProps) {
  const [draft,       setDraft]       = useState<Draft | null>(null)
  const [plan,        setPlan]        = useState<BulkProgramResult | null>(null)
  const [busy,        setBusy]        = useState(false)
  const [error,       setError]       = useState<string | null>(null)
  const [genres,      setGenres]      = useState<ProgramGenre[]>([])
  const [collections, setCollections] = useState<Collection[]>([])
  const [series,      setSeries]      = useState<Series[]>([])

  const kind = draft?.kind

  useEffect(() => {
    if (!kind) return
    let cancelled = false
    void (async () => {
      try {
        if (kind === 'GENRE')      setGenres(await programGenresApi.list({ channelId }))
        if (kind === 'COLLECTION') setCollections(await collectionsApi.list({ channelId }))
        if (kind === 'SERIES')     setSeries(await seriesApi.list({ channelId }))
      } catch {
        if (!cancelled) setError('Chargement impossible')
      }
    })()
    return () => { cancelled = true }
  }, [kind, channelId])

  // Le plan est invalidé dès que le brouillon change : un aperçu calculé pour
  // un genre puis affiché pour un autre mentirait sur ce qui sera écrit.
  useEffect(() => { setPlan(null); setError(null) }, [draft])

  const preview = useCallback(async () => {
    if (!draft) return
    const action = toAction(draft)
    if (!action) return
    setBusy(true); setError(null)
    try {
      setPlan(await programsApi.bulk({ channelId, programIds: selectedIds, action, dryRun: true }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aperçu impossible')
    } finally {
      setBusy(false)
    }
  }, [draft, channelId, selectedIds])

  const apply = useCallback(async () => {
    if (!draft) return
    const action = toAction(draft)
    if (!action) return
    setBusy(true); setError(null)
    try {
      await programsApi.bulk({ channelId, programIds: selectedIds, action })
      setDraft(null)
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action impossible')
    } finally {
      setBusy(false)
    }
  }, [draft, channelId, selectedIds, onDone])

  const close = useCallback(() => setDraft(null), [])

  if (selectedIds.length === 0) return null

  return (
    <>
      <div className="catalog-bulk-actions">
        <button type="button" className="tp-button tp-button--xs" onClick={() => setDraft({ kind: 'GENRE', programGenreId: '', subGenre: '' })}>
          Genre
        </button>
        <button type="button" className="tp-button tp-button--xs" onClick={() => setDraft({ kind: 'AIR_DATE', date: '' })}>
          Date de diffusion
        </button>
        {allowSeriesChange && (
          <button type="button" className="tp-button tp-button--xs" onClick={() => setDraft({ kind: 'SERIES', seriesId: '' })}>
            Série
          </button>
        )}
        <button type="button" className="tp-button tp-button--xs" onClick={() => setDraft({ kind: 'COLLECTION', collectionId: '', mode: 'ADD' })}>
          Collection
        </button>
        <button
          type="button"
          className="tp-button tp-button--xs"
          onClick={() => setDraft({ kind: 'ARCHIVE' })}
          disabled={!canDelete}
          title={!canDelete ? 'Permission de retrait des programmes requise' : undefined}
        >
          Archiver
        </button>
        {selectionHasArchived && (
          <button
            type="button"
            className="tp-button tp-button--xs"
            onClick={() => setDraft({ kind: 'RESTORE' })}
            disabled={!canDelete}
            title={!canDelete ? 'Permission de retrait des programmes requise' : 'Remettre les fiches archivées dans le catalogue'}
          >
            Désarchiver
          </button>
        )}
      </div>

      {draft && (
        <ModalShell title={DRAFT_TITLES[draft.kind]} size="narrow" onClose={close} closeDisabled={busy}>
          <div className="tp-modal-form">
            {error && <div className="tp-feedback tp-feedback--error" role="alert">{error}</div>}

            <p className="tp-field-help">
              {selectedIds.length} programme{selectedIds.length > 1 ? 's' : ''} sélectionné{selectedIds.length > 1 ? 's' : ''}.
            </p>

            {draft.kind === 'GENRE' && (
              <>
                <div className="tp-form-field">
                  <label className="tp-field-label" htmlFor="bulk-genre">Genre</label>
                  <select
                    id="bulk-genre"
                    className="tp-input"
                    value={draft.programGenreId}
                    onChange={e => setDraft({ ...draft, programGenreId: e.target.value })}
                  >
                    <option value="">Choisir un genre…</option>
                    {genres.map(genre => <option key={genre.id} value={genre.id}>{genre.label}</option>)}
                  </select>
                </div>
                <div className="tp-form-field">
                  <label className="tp-field-label" htmlFor="bulk-subgenre">Sous-genre</label>
                  <input
                    id="bulk-subgenre"
                    className="tp-input"
                    value={draft.subGenre}
                    onChange={e => setDraft({ ...draft, subGenre: e.target.value })}
                    placeholder="Laisser vide pour ne pas y toucher"
                  />
                  <p className="tp-field-help">
                    Un champ vide conserve les sous-genres déjà saisis sur les fiches.
                  </p>
                </div>
              </>
            )}

            {draft.kind === 'AIR_DATE' && (
              <div className="tp-form-field">
                <label className="tp-field-label" htmlFor="bulk-air-date">Date de première diffusion</label>
                <input
                  id="bulk-air-date"
                  type="date"
                  className="tp-input"
                  value={draft.date}
                  onChange={e => setDraft({ ...draft, date: e.target.value })}
                />
                <p className="tp-field-help">Laisser vide retire la date des fiches sélectionnées.</p>
              </div>
            )}

            {draft.kind === 'SERIES' && (
              <div className="tp-form-field">
                <label className="tp-field-label" htmlFor="bulk-series">Série</label>
                <select
                  id="bulk-series"
                  className="tp-input"
                  value={draft.seriesId}
                  onChange={e => setDraft({ ...draft, seriesId: e.target.value })}
                >
                  <option value="">Détacher de toute série</option>
                  {series.map(item => <option key={item.id} value={item.id}>{item.title}</option>)}
                </select>
              </div>
            )}

            {draft.kind === 'COLLECTION' && (
              <>
                <div className="tp-form-field">
                  <label className="tp-field-label" htmlFor="bulk-collection">Collection</label>
                  <select
                    id="bulk-collection"
                    className="tp-input"
                    value={draft.collectionId}
                    onChange={e => setDraft({ ...draft, collectionId: e.target.value })}
                  >
                    <option value="">Choisir une collection…</option>
                    {collections.map(collection => <option key={collection.id} value={collection.id}>{collection.name}</option>)}
                  </select>
                </div>
                <div className="tp-form-field">
                  <label className="tp-field-label" htmlFor="bulk-collection-mode">Action</label>
                  <select
                    id="bulk-collection-mode"
                    className="tp-input"
                    value={draft.mode}
                    onChange={e => setDraft({ ...draft, mode: e.target.value as 'ADD' | 'REMOVE' })}
                  >
                    <option value="ADD">Ajouter à la collection</option>
                    <option value="REMOVE">Retirer de la collection</option>
                  </select>
                </div>
              </>
            )}

            {draft.kind === 'ARCHIVE' && (
              <p className="tp-field-help">
                Les fiches archivées quittent le catalogue actif. Celles qui sont à l’antenne
                sont épargnées et vous seront listées.
              </p>
            )}

            {draft.kind === 'RESTORE' && (
              <p className="tp-field-help">
                Les fiches reviennent dans le catalogue actif et redeviennent programmables.
                Celles qui y sont déjà vous seront listées comme écartées.
              </p>
            )}

            {plan && (
              <div className="tp-feedback tp-feedback--info" role="status">
                <strong>{plan.targetIds.length}</strong> programme{plan.targetIds.length > 1 ? 's' : ''} modifié{plan.targetIds.length > 1 ? 's' : ''}.
                {plan.skipped.length > 0 && (
                  <>
                    <br />
                    {plan.skipped.length} écarté{plan.skipped.length > 1 ? 's' : ''} :
                    <ul className="catalog-bulk-plan__skipped">
                      {plan.skipped.slice(0, 8).map(skip => (
                        <li key={skip.id}>{skip.title ?? skip.id} — {BULK_SKIP_LABELS[skip.reason]}</li>
                      ))}
                      {plan.skipped.length > 8 && (
                        <li>… et {plan.skipped.length - 8} autre{plan.skipped.length - 8 > 1 ? 's' : ''}</li>
                      )}
                    </ul>
                  </>
                )}
              </div>
            )}

            <div className="tp-modal-actions">
              <button type="button" onClick={close} className="tp-button" disabled={busy}>
                Annuler
              </button>
              {plan
                ? (
                  <button
                    type="button"
                    onClick={() => void apply()}
                    className="tp-button tp-button--info"
                    disabled={busy || plan.targetIds.length === 0}
                    data-state={busy ? 'loading' : undefined}
                  >
                    {busy ? 'Application…' : `Appliquer à ${plan.targetIds.length} programme${plan.targetIds.length > 1 ? 's' : ''}`}
                  </button>
                )
                : (
                  <button
                    type="button"
                    onClick={() => void preview()}
                    className="tp-button tp-button--info"
                    disabled={busy || !toAction(draft)}
                    data-state={busy ? 'loading' : undefined}
                  >
                    {busy ? 'Calcul…' : 'Vérifier'}
                  </button>
                )}
            </div>
          </div>
        </ModalShell>
      )}
    </>
  )
})
